import React, { useState, useEffect } from "react";
import { API_URL } from "../../config";
import axios from "axios";

const Broadcast = () => {
  const [filters, setFilters] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState("");
  const [selectedOffice, setSelectedOffice] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sendingMethod, setSendingMethod] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailIds, setEmailIds] = useState([]);
  const [mobileNumbers, setMobileNumbers] = useState([]);
  const [emailAttachment, setEmailAttachment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Location filters
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedAssembly, setSelectedAssembly] = useState("");

  const [smsMessage, setSmsMessage] = useState("");
  const [smsCharCount, setSmsCharCount] = useState(160);
  const [smsMessageParts, setSmsMessageParts] = useState(1);

  const getAuthToken = () => localStorage.getItem("token");

  useEffect(() => {
    fetchFilters();
    fetchOffices();
    fetchLocations();
  }, []);

  // Fetch states and locations
  const fetchLocations = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: null, dist_name1: null },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setStates(response.data.states || []);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  // Fetch districts for selected state
  const fetchDistricts = async (state) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setDistricts(response.data.districts || []);
        setAssemblies([]);
        setSelectedDistrict("");
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  // Fetch assemblies for selected district
  const fetchAssemblies = async (state, district) => {
    try {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_URL}locations`,
        { state_name: state, dist_name1: district },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAssemblies(response.data.assemblies || []);
        setSelectedAssembly("");
      }
    } catch (error) {
      console.error("Error fetching assemblies:", error);
    }
  };

  // Handle state change
  const handleStateChange = (e) => {
    const state = e.target.value;
    setSelectedState(state);
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);
    
    if (state) {
      fetchDistricts(state);
    }
  };

  // Handle district change
  const handleDistrictChange = (e) => {
    const district = e.target.value;
    setSelectedDistrict(district);
    setSelectedAssembly("");
    setAssemblies([]);
    
    if (district && selectedState) {
      fetchAssemblies(selectedState, district);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await fetch(`${API_URL}fatchDropdownValue`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      const data = await response.json();
      if (data.status === "success") {
        setFilters(data.data);
      }
    } catch (error) {
      showNotification("Error fetching filters", "error");
    }
  };

  const fetchOffices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}getOfficesList`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === "success") {
        setOffices(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching offices:", error);
    }
  };

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleFilterChange = async () => {
    if (!selectedFilter) {
      showNotification("Please select a filter", "error");
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append("filter", selectedFilter);
    if (startDate) formData.append("start_date", startDate);
    if (endDate) formData.append("end_date", endDate);
    
    // Only include office_id if not "all"
    if (selectedOffice !== "all") {
      formData.append("office_id", selectedOffice);
    }

    // Add location filters if selected
    if (selectedState) {
      formData.append("state", selectedState);
    }
    if (selectedDistrict) {
      formData.append("district", selectedDistrict);
    }
    if (selectedAssembly) {
      formData.append("assembly", selectedAssembly);
    }

    try {
      const response = await fetch(`${API_URL}FilterData`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: formData,
      });
      const data = await response.json();

      if (data.status === "success") {
        // Extract email IDs and mobile numbers
        const emails = data.data
          .map((item) => item.email_id)
          .filter(email => email && email.trim() !== ""); // Filter out empty/null emails
        const mobiles = data.data
          .map((item) => item.mobile_number)
          .filter(mobile => mobile && mobile.trim() !== ""); // Filter out empty/null mobiles
        
        setEmailIds(emails);
        setMobileNumbers(mobiles);
        
        // Show counts in notification
        const emailCount = emails.length;
        const mobileCount = mobiles.length;
        
        let message = `Filter applied successfully. Found ${data.data.length} records.`;
        if (emailCount > 0) {
          message += ` ${emailCount} email address${emailCount > 1 ? 'es' : ''} found.`;
        }
        if (mobileCount > 0) {
          message += ` ${mobileCount} mobile number${mobileCount > 1 ? 's' : ''} found.`;
        }
        
        showNotification(message);
        
        // Log counts for debugging
        console.log(`Found ${emailCount} emails and ${mobileCount} mobile numbers`);
      } else {
        showNotification("No data found for the selected filters", "warning");
        setEmailIds([]);
        setMobileNumbers([]);
      }
    } catch (error) {
      console.error("Filter error:", error);
      showNotification("Error applying filter", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSmsChange = (e) => {
    const message = e.target.value;
    setSmsMessage(message);
    const isUnicode = /[\u{0080}-\u{FFFF}]/u.test(message);
    const charLimit = isUnicode ? 70 : 160;
    const remainingChars = charLimit - (message.length % charLimit);
    const messageParts = Math.ceil(message.length / charLimit);
    setSmsCharCount(remainingChars);
    setSmsMessageParts(messageParts);
  };

  const handleEmailSubmit = async () => {
    if (!emailSubject || !emailMessage) {
      showNotification("Please fill in all required fields", "error");
      return;
    }

    if (!Array.isArray(emailIds) || emailIds.length === 0) {
      showNotification("No valid email addresses found. Please apply filters first.", "error");
      return;
    }

    const validEmails = emailIds.filter(email => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return email && emailRegex.test(email);
    });

    if (validEmails.length === 0) {
      showNotification("No valid email addresses to send to", "error");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("subject", emailSubject);
      formData.append("message", emailMessage);

      // Send all valid emails
      validEmails.forEach((email) => {
        formData.append("to[]", email);
      });

      if (emailAttachment) {
        formData.append("attachment", emailAttachment);
      }

      const response = await fetch(`${API_URL}send-group-email`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });

      const result = await response.json();
      
      if (response.ok && result.status === "success") {
        showNotification(`Email sent successfully to ${validEmails.length} recipients!`);
        setEmailSubject("");
        setEmailMessage("");
        setEmailAttachment(null);
      } else {
        showNotification(result.message || "Failed to send email", "error");
      }
    } catch (error) {
      console.error("Email sending error:", error);
      showNotification("Error sending email", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSmsSubmit = async () => {
    if (!smsMessage) {
      showNotification("Please enter a message", "error");
      return;
    }

    if (!Array.isArray(mobileNumbers) || mobileNumbers.length === 0) {
      showNotification("No valid mobile numbers found. Please apply filters first.", "error");
      return;
    }

    const validMobiles = mobileNumbers.filter(mobile => {
      const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number validation
      return mobile && mobileRegex.test(mobile.toString().trim());
    });

    if (validMobiles.length === 0) {
      showNotification("No valid mobile numbers to send SMS to", "error");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("message", smsMessage);
      
      // Send all valid mobile numbers
      validMobiles.forEach((mobile) => {
        formData.append("mobile[]", mobile);
      });

      const response = await fetch(`${API_URL}send-group-sms`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });

      const result = await response.json();
      
      if (response.ok && result.status === "success") {
        showNotification(`SMS sent successfully to ${validMobiles.length} recipients!`);
        setSmsMessage("");
      } else {
        showNotification(result.message || "Failed to send SMS", "error");
      }
    } catch (error) {
      console.error("SMS sending error:", error);
      showNotification("Error sending SMS", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedFilter("");
    setSelectedOffice("all");
    setStartDate("");
    setEndDate("");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedAssembly("");
    setDistricts([]);
    setAssemblies([]);
    setEmailIds([]);
    setMobileNumbers([]);
    showNotification("Filters reset successfully");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-green-600 to-green-800 p-6">
            <h1 className="text-3xl font-bold text-white text-center">
              Message Broadcast System
            </h1>
          </div>

          <div className="p-6 space-y-6">
            {/* Filter Section */}
            <div className="bg-gray-50 rounded-xl p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Filter Options
                </h2>
                <button
                  onClick={handleResetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Reset All Filters
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Filter
                  </label>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select Filter Type</option>
                    {filters.map((item, index) => (
                      <option key={index} value={Object.values(item)[0]}>
                        {Object.values(item)[0]}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Office
                  </label>
                  <select
                    value={selectedOffice}
                    onChange={(e) => setSelectedOffice(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  >
                    <option value="all">All Offices</option>
                    {offices.map((office) => (
                      <option key={office.id} value={office.id}>
                        {office.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={handleStateChange}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                  >
                    <option value="">All States</option>
                    {states.map((state, index) => (
                      <option key={index} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    District
                  </label>
                  <select
                    value={selectedDistrict}
                    onChange={handleDistrictChange}
                    disabled={!selectedState}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">All Districts</option>
                    {districts.map((district, index) => (
                      <option key={index} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assembly
                  </label>
                  <select
                    value={selectedAssembly}
                    onChange={(e) => setSelectedAssembly(e.target.value)}
                    disabled={!selectedDistrict}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">All Assemblies</option>
                    {assemblies.map((assembly, index) => (
                      <option key={index} value={assembly}>
                        {assembly}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Recipients Info */}
              {(emailIds.length > 0 || mobileNumbers.length > 0) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-green-800 mb-2">
                    Recipients Found:
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-800 text-xs font-bold">
                        {emailIds.length}
                      </span>
                      <span className="text-sm text-green-700">
                        Email Address{emailIds.length !== 1 ? 'es' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                        {mobileNumbers.length}
                      </span>
                      <span className="text-sm text-blue-700">
                        Mobile Number{mobileNumbers.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={handleFilterChange}
                disabled={isLoading || !selectedFilter}
                className="w-full bg-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Applying..." : "Apply Filter"}
              </button>
            </div>

            {/* Sending Method Selection */}
            <div className="bg-gray-50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                Sending Method
              </h2>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="sending_method"
                    value="email"
                    onChange={() => setSendingMethod("email")}
                    checked={sendingMethod === "email"}
                    className="w-5 h-5 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-lg text-gray-700 group-hover:text-green-600">
                    Email
                  </span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer group">
                  <input
                    type="radio"
                    name="sending_method"
                    value="sms"
                    onChange={() => setSendingMethod("sms")}
                    checked={sendingMethod === "sms"}
                    className="w-5 h-5 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-lg text-gray-700 group-hover:text-green-600">
                    SMS
                  </span>
                </label>
              </div>
            </div>

            {/* Email Form */}
            {sendingMethod === "email" && (
              <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Compose Email
                </h2>
                
                {emailIds.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      This email will be sent to <strong>{emailIds.length}</strong> recipient{emailIds.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter email subject..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="5"
                    placeholder="Write your message..."
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachment (Optional)
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setEmailAttachment(e.target.files[0])}
                    className="w-full file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-green-600 file:text-white hover:file:bg-green-700 file:cursor-pointer"
                  />
                </div>
                <button
                  onClick={handleEmailSubmit}
                  disabled={isLoading || !emailSubject || !emailMessage || emailIds.length === 0}
                  className="w-full bg-green-600 text-white font-semibold py-3 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending..." : `Send Email to ${emailIds.length} Recipients`}
                </button>
              </div>
            )}

            {/* SMS Form */}
            {sendingMethod === "sms" && (
              <div className="bg-gray-50 rounded-xl p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-800">
                  Compose SMS
                </h2>
                
                {mobileNumbers.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-700">
                      This SMS will be sent to <strong>{mobileNumbers.length}</strong> recipient{mobileNumbers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={smsMessage}
                    onChange={handleSmsChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    rows="5"
                    placeholder="Type your SMS message..."
                  ></textarea>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>
                      Characters left: <strong>{smsCharCount}</strong>
                    </span>
                    <span>
                      Message parts: <strong>{smsMessageParts}</strong>
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Unicode messages: 70 characters per part
                    <br />
                    Regular messages: 160 characters per part
                  </p>
                </div>
                <button
                  onClick={handleSmsSubmit}
                  disabled={isLoading || !smsMessage || mobileNumbers.length === 0}
                  className="w-full bg-green-600 text-white font-semibold py-3 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Sending..." : `Send SMS to ${mobileNumbers.length} Recipients`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg transform transition-all duration-500 z-50 ${
              notification.type === "error"
                ? "bg-red-500 text-white"
                : notification.type === "warning"
                ? "bg-yellow-500 text-white"
                : "bg-green-500 text-white"
            }`}
          >
            <div className="flex items-center gap-2">
              {notification.type === "success" && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              )}
              {notification.type === "error" && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              )}
              {notification.type === "warning" && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L4.332 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              )}
              <span>{notification.message}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Broadcast;